/**
 * Blog Post 4: The True Cost of a HKD 5 Million Mortgage: Beyond the Monthly Payment
 * 500萬港元按揭的真實成本：不只是每月供款
 */
import { useEffect } from 'react';
import { useCustomTranslation } from '../hooks';
import { Box, Container, Typography } from '@mui/material';
import { trackPageView } from '../utils/analytics';
import AmazonRecommendation from '../components/common/AmazonRecommendation';
import SEO from '../components/common/SEO';

export default function BlogPost4() {
  const { i18n } = useCustomTranslation();
  const isEnglish = i18n.language === 'en';

  useEffect(() => {
    trackPageView('/blog/true-cost-5-million-mortgage-hong-kong-2026', 'Blog Post 4');
    document.title = isEnglish
      ? 'The True Cost of a HKD 5 Million Mortgage: Beyond the Monthly Payment | PayWise365'
      : '500萬港元按揭的真實成本：不只是每月供款 | PayWise365';
  }, [isEnglish]);

  const seoTitle = isEnglish
    ? 'The True Cost of a HKD 5 Million Mortgage: Beyond the Monthly Payment | PayWise365'
    : '500萬港元按揭的真實成本：不只是每月供款 | PayWise365';

  const seoDescription = isEnglish
    ? 'Discover the total cost of a HK$5 million mortgage over 30 years. Learn about hidden fees, insurance costs, opportunity costs, and how interest rates dramatically affect your total payment in 2026.'
    : '全面分析500萬港元按揭30年的真實成本。了解隱藏費用、保險開支、機會成本，以及2026年利率如何影響總還款額。';

  const seoKeywords = isEnglish
    ? '5 million mortgage Hong Kong, 500萬按揭, mortgage total cost, total interest payment, mortgage fees HK, mortgage insurance, true cost analysis, 按揭成本分析'
    : '500萬按揭, 香港按揭成本, 總利息支出, 按揭隱藏費用, 按揭保險, 真實成本';

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonicalUrl="https://www.paywise365.com/blog/true-cost-5-million-mortgage-hong-kong-2026"
        ogType="article"
        publishDate="2026-02-07T00:00:00+08:00"
        modifiedDate="2026-02-07T00:00:00+08:00"
        articleSection={isEnglish ? 'Mortgage Cost Analysis' : '按揭成本分析'}
        tags={isEnglish ? ['Mortgage Cost', 'Financial Planning', 'Interest Calculation'] : ['按揭成本', '理財規劃', '利息計算']}
      />
      {/* Page Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h2" sx={{ mb: 2, fontWeight: 700 }}>
            {isEnglish ? 'The True Cost of a HKD 5 Million Mortgage: Beyond the Monthly Payment' : '500萬港元按揭的真實成本：不只是每月供款'}
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.95, fontWeight: 400 }}>
            {isEnglish ? 'A complete breakdown of all costs over 25 years' : '25年期間所有成本的完整分析'}
          </Typography>
        </Container>
      </Box>

      {/* Article Content */}
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        <Box
          component="article"
          sx={{
            lineHeight: 1.8,
            '& p': {
              mb: 2,
            },
            '& h2': {
              mt: 4,
              mb: 2,
              fontSize: '1.75rem',
              fontWeight: 700,
            },
            '& h3': {
              mt: 3,
              mb: 1.5,
              fontSize: '1.5rem',
              fontWeight: 600,
            },
            '& ul, & ol': {
              mb: 2,
              pl: 3,
            },
            '& li': {
              mb: 1,
            },
          }}
        >
          {isEnglish ? (
            // English Content
            <>
              <div style={{ marginBottom: '30px', paddingBottom: '20px', borderBottom: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '10px' }}>
                  <span style={{
                    background: '#eff6ff',
                    color: '#2563eb',
                    padding: '6px 14px',
                    borderRadius: '14px',
                    fontWeight: '500',
                    marginRight: '12px'
                  }}>
                    Mortgage Planning
                  </span>
                  <span>Published: January 31, 2026</span>
                  <span style={{ margin: '0 8px' }}>•</span>
                  <span>Reading Time: 10 minutes</span>
                </div>
              </div>

              <h2>💰 The Sticker Shock: What You Really Pay</h2>
              <p>
                You're buying a HKD 5 million property with a 10% down payment. Your monthly payment? Around HKD 18,200. Manageable, right? But here's the reality check: <strong>over 25 years, you'll pay approximately HKD 8.4 million for that HKD 5 million property</strong>—nearly 70% more than the purchase price.
              </p>
              <p>
                Let's break down exactly where every dollar goes, so you can make informed decisions about your biggest financial commitment.
              </p>

              <div style={{
                background: '#fef3c7',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #fbbf24',
                margin: '30px 0'
              }}>
                <h3 style={{ marginTop: 0 }}>💡 Quick Summary (TL;DR)</h3>
                <p><strong>For a HKD 5M property (HKD 4.5M loan at 3.875%, 25 years):</strong></p>
                <ul>
                  <li>Down Payment: HKD 500,000</li>
                  <li>Total Interest: HKD 2,336,000</li>
                  <li>Stamp Duty: HKD 168,750 (first-time buyer)</li>
                  <li>Legal & Bank Fees: HKD 65,000</li>
                  <li>Mortgage Insurance: HKD 157,500</li>
                  <li><strong>GRAND TOTAL: HKD 7,727,250</strong></li>
                </ul>
                <p style={{ marginBottom: 0 }}>
                  That's <strong>54.5% more</strong> than the property price!
                </p>
              </div>

              <h2>📊 Complete Cost Breakdown: HKD 5 Million Property</h2>

              <h3>Scenario: First-Time Homebuyer in 2026</h3>
              <div style={{
                background: '#f0f9ff',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #7dd3fc',
                margin: '20px 0'
              }}>
                <ul>
                  <li><strong>Property Price:</strong> HKD 5,000,000</li>
                  <li><strong>Down Payment:</strong> 10% (HKD 500,000)</li>
                  <li><strong>Loan Amount:</strong> HKD 4,500,000</li>
                  <li><strong>Interest Rate:</strong> 3.875% (typical 2026 H-Rate with cap)</li>
                  <li><strong>Loan Term:</strong> 25 years (300 months)</li>
                  <li><strong>Monthly Payment:</strong> HKD 23,387</li>
                </ul>
              </div>

              <h2>💸 1. The Principal: What You're Borrowing</h2>
              <div style={{
                background: '#f8fafc',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                margin: '20px 0'
              }}>
                <p><strong>Loan Amount: HKD 4,500,000</strong></p>
                <p>This is straightforward—it's the money the bank lends you. Over 25 years, you pay this back through your monthly installments.</p>
                <p style={{ marginBottom: 0 }}>
                  <strong>Running Total: HKD 4,500,000</strong>
                </p>
              </div>

              <h2>🏦 2. Interest: The Bank's Profit</h2>
              <div style={{
                background: '#f8fafc',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                margin: '20px 0'
              }}>
                <p><strong>Total Interest (25 years at 3.875%): HKD 2,516,100</strong></p>
                <p><strong>That's 56% of your loan amount!</strong></p>

                <h4>Interest Payment Breakdown by Year:</h4>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', marginTop: '15px' }}>
                  <thead>
                    <tr style={{ background: '#eff6ff' }}>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #2563eb' }}>Period</th>
                      <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #2563eb' }}>Interest Paid</th>
                      <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #2563eb' }}>% of Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: '10px', borderBottom: '1px solid #e2e8f0' }}>Years 1-5</td>
                      <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>HKD 815,000</td>
                      <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>32.4%</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '10px', borderBottom: '1px solid #e2e8f0' }}>Years 6-10</td>
                      <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>HKD 705,000</td>
                      <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>28.0%</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '10px', borderBottom: '1px solid #e2e8f0' }}>Years 11-15</td>
                      <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>HKD 565,000</td>
                      <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>22.5%</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '10px', borderBottom: '1px solid #e2e8f0' }}>Years 16-20</td>
                      <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>HKD 330,000</td>
                      <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>13.1%</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '10px' }}>Years 21-25</td>
                      <td style={{ padding: '10px', textAlign: 'right' }}>HKD 101,000</td>
                      <td style={{ padding: '10px', textAlign: 'right' }}>4.0%</td>
                    </tr>
                  </tbody>
                </table>

                <p style={{ marginTop: '15px', marginBottom: 0 }}>
                  <strong>Key Insight:</strong> You pay the most interest in the early years. This is why partial repayment early in your mortgage term saves you the most money!
                </p>
                <p style={{ marginBottom: 0, marginTop: '15px' }}>
                  <strong>Running Total: HKD 7,016,100</strong>
                </p>
              </div>

              <h2>📋 3. Stamp Duty: Government Tax</h2>
              <div style={{
                background: '#f8fafc',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                margin: '20px 0'
              }}>
                <p><strong>First-Time Buyer (Ad Valorem Stamp Duty - Scale 2): HKD 168,750 (3.375%)</strong></p>

                <h4>Hong Kong Stamp Duty Rates (2026):</h4>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', marginTop: '15px' }}>
                  <thead>
                    <tr style={{ background: '#eff6ff' }}>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #2563eb' }}>Property Value</th>
                      <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #2563eb' }}>First-Time Buyer</th>
                      <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #2563eb' }}>Second Property</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: '10px', borderBottom: '1px solid #e2e8f0' }}>Up to HKD 3M</td>
                      <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>HKD 100</td>
                      <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>15%</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '10px', borderBottom: '1px solid #e2e8f0' }}>HKD 3M - 4.5M</td>
                      <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>1.5%</td>
                      <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>15%</td>
                    </tr>
                    <tr style={{ background: '#dcfce7' }}>
                      <td style={{ padding: '10px', borderBottom: '1px solid #e2e8f0' }}><strong>HKD 4.5M - 6M</strong></td>
                      <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}><strong>3.375%</strong></td>
                      <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}><strong>15%</strong></td>
                    </tr>
                    <tr>
                      <td style={{ padding: '10px', borderBottom: '1px solid #e2e8f0' }}>HKD 6M - 20M</td>
                      <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>4.25%</td>
                      <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>15%</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '10px' }}>Over HKD 20M</td>
                      <td style={{ padding: '10px', textAlign: 'right' }}>4.25%</td>
                      <td style={{ padding: '10px', textAlign: 'right' }}>15%</td>
                    </tr>
                  </tbody>
                </table>

                <div style={{
                  background: '#fef3c7',
                  padding: '15px',
                  borderRadius: '8px',
                  marginTop: '15px'
                }}>
                  <p style={{ marginTop: 0 }}><strong>⚠️ Important:</strong></p>
                  <p style={{ marginBottom: 0 }}>
                    If this is your <strong>second residential property</strong>, you'd pay <strong>HKD 750,000 (15%)</strong> instead—that's HKD 581,250 more! This is why property investors need much deeper pockets.
                  </p>
                </div>

                <p style={{ marginBottom: 0, marginTop: '15px' }}>
                  <strong>Running Total: HKD 7,184,850</strong>
                </p>
              </div>

              <h2>⚖️ 4. Legal Fees: Conveyancing Costs</h2>
              <div style={{
                background: '#f8fafc',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                margin: '20px 0'
              }}>
                <p><strong>Typical Range: HKD 15,000 - 25,000</strong></p>
                <p><strong>Our Estimate: HKD 20,000</strong></p>

                <h4>What This Covers:</h4>
                <ul>
                  <li>Title search and verification</li>
                  <li>Sale & purchase agreement review</li>
                  <li>Mortgage deed preparation</li>
                  <li>Land Registry filing</li>
                  <li>Legal representation at completion</li>
                </ul>

                <p><strong>Tip:</strong> Shop around—legal fees can vary significantly. Ask your bank for lawyer recommendations, as they often have negotiated rates.</p>

                <p style={{ marginBottom: 0 }}>
                  <strong>Running Total: HKD 7,204,850</strong>
                </p>
              </div>

              <h2>🏢 5. Bank & Processing Fees</h2>
              <div style={{
                background: '#f8fafc',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                margin: '20px 0'
              }}>
                <p><strong>Typical Total: HKD 30,000 - 50,000</strong></p>
                <p><strong>Our Estimate: HKD 40,000</strong></p>

                <h4>Breakdown:</h4>
                <ul>
                  <li><strong>Property Valuation:</strong> HKD 3,000 - 5,000</li>
                  <li><strong>Mortgage Application Fee:</strong> HKD 5,000 - 10,000 (often waived)</li>
                  <li><strong>Fire Insurance:</strong> HKD 2,000 - 3,000 per year × 25 years = HKD 50,000 - 75,000 (averaged HKD 15,000 upfront)</li>
                  <li><strong>Account Opening/Maintenance:</strong> Usually waived if you maintain minimum balance</li>
                </ul>

                <p><strong>Good News:</strong> Many banks waive application fees during promotional periods. The cash rebate (typically 0.5-1.2% of loan amount) can offset some of these costs—potentially HKD 22,500-54,000 back!</p>

                <p style={{ marginBottom: 0 }}>
                  <strong>Running Total (net of rebate): HKD 7,222,350</strong>
                </p>
              </div>

              <h2>🛡️ 6. Mortgage Insurance Premium (If Applicable)</h2>
              <div style={{
                background: '#f8fafc',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                margin: '20px 0'
              }}>
                <p><strong>For 90% LTV (HKD 4.5M loan on HKD 5M property): HKD 157,500 (3.5% of loan amount)</strong></p>

                <h4>HKMC Insurance Premiums (2026):</h4>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', marginTop: '15px' }}>
                  <thead>
                    <tr style={{ background: '#eff6ff' }}>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #2563eb' }}>LTV Ratio</th>
                      <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #2563eb' }}>25-Year Term</th>
                      <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #2563eb' }}>30-Year Term</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: '10px', borderBottom: '1px solid #e2e8f0' }}>80-85%</td>
                      <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>2.15%</td>
                      <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>2.47%</td>
                    </tr>
                    <tr style={{ background: '#dcfce7' }}>
                      <td style={{ padding: '10px', borderBottom: '1px solid #e2e8f0' }}><strong>85-90%</strong></td>
                      <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}><strong>3.5%</strong></td>
                      <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}><strong>4.35%</strong></td>
                    </tr>
                  </tbody>
                </table>

                <p style={{ marginTop: '15px' }}><strong>Note:</strong> If you can afford a 20% down payment (80% LTV), you can avoid mortgage insurance entirely, saving HKD 157,500!</p>

                <p style={{ marginBottom: 0 }}>
                  <strong>Final Total: HKD 7,379,850</strong>
                </p>
              </div>

              <h2>📊 The Final Tally: Complete Cost Summary</h2>
              <div style={{
                background: '#eff6ff',
                padding: '25px',
                borderRadius: '12px',
                border: '2px solid #2563eb',
                margin: '30px 0'
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '15px' }}>
                  <thead>
                    <tr style={{ background: '#dbeafe' }}>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #2563eb' }}>Cost Item</th>
                      <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #2563eb' }}>Amount (HKD)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #bfdbfe' }}>Down Payment (10%)</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #bfdbfe' }}>500,000</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #bfdbfe' }}>Loan Principal</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #bfdbfe' }}>4,500,000</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #bfdbfe' }}>Total Interest (25 years)</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #bfdbfe', color: '#dc2626', fontWeight: 'bold' }}>2,516,100</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #bfdbfe' }}>Stamp Duty (first-time)</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #bfdbfe' }}>168,750</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #bfdbfe' }}>Legal Fees</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #bfdbfe' }}>20,000</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #bfdbfe' }}>Bank & Processing Fees</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #bfdbfe' }}>40,000</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #bfdbfe' }}>Mortgage Insurance (90% LTV)</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #bfdbfe' }}>157,500</td>
                    </tr>
                    <tr style={{ background: '#dbeafe' }}>
                      <td style={{ padding: '15px', fontWeight: 'bold', fontSize: '16px' }}>GRAND TOTAL</td>
                      <td style={{ padding: '15px', textAlign: 'right', fontWeight: 'bold', fontSize: '18px', color: '#1e40af' }}>7,902,350</td>
                    </tr>
                  </tbody>
                </table>

                <div style={{
                  background: '#fef3c7',
                  padding: '20px',
                  borderRadius: '8px',
                  marginTop: '25px'
                }}>
                  <h3 style={{ marginTop: 0 }}>🎯 Key Insight:</h3>
                  <p style={{ fontSize: '18px', marginBottom: 0 }}>
                    You pay <strong>HKD 7,902,350</strong> for a <strong>HKD 5,000,000</strong> property—that's <strong>58% more</strong> than the purchase price, or <strong>HKD 2,902,350</strong> in additional costs!
                  </p>
                </div>
              </div>

              <h2>💡 How to Reduce Your Total Cost</h2>

              <div style={{
                background: '#dcfce7',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #10b981',
                margin: '20px 0'
              }}>
                <h3 style={{ marginTop: 0 }}>Strategy 1: Larger Down Payment (20% instead of 10%)</h3>
                <ul>
                  <li>Loan amount: HKD 4M (instead of HKD 4.5M)</li>
                  <li>Interest saved: ~HKD 560,000</li>
                  <li>Mortgage insurance saved: HKD 157,500</li>
                  <li><strong>Total savings: HKD 717,500</strong></li>
                </ul>
              </div>

              <div style={{
                background: '#dcfce7',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #10b981',
                margin: '20px 0'
              }}>
                <h3 style={{ marginTop: 0 }}>Strategy 2: Shorter Loan Term (20 years instead of 25)</h3>
                <ul>
                  <li>Monthly payment: HKD 27,368 (vs HKD 23,387)</li>
                  <li>Total interest: HKD 2,068,320 (vs HKD 2,516,100)</li>
                  <li><strong>Interest saved: HKD 447,780</strong></li>
                  <li><strong>Own your home 5 years earlier!</strong></li>
                </ul>
              </div>

              <div style={{
                background: '#dcfce7',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #10b981',
                margin: '20px 0'
              }}>
                <h3 style={{ marginTop: 0 }}>Strategy 3: Lower Interest Rate (0.5% reduction)</h3>
                <ul>
                  <li>From 3.875% to 3.375%</li>
                  <li>Monthly payment: HKD 22,367 (save HKD 1,020/month)</li>
                  <li>Total interest: HKD 2,220,100 (vs HKD 2,516,100)</li>
                  <li><strong>Interest saved: HKD 296,000</strong></li>
                </ul>
                <p style={{ marginBottom: 0 }}>
                  <strong>Tip:</strong> Shop around! Apply to 2-3 banks to get the best rate.
                </p>
              </div>

              <div style={{
                background: '#dcfce7',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #10b981',
                margin: '20px 0'
              }}>
                <h3 style={{ marginTop: 0 }}>Strategy 4: Partial Repayment in Year 5</h3>
                <ul>
                  <li>Make HKD 500,000 partial repayment after 5 years</li>
                  <li>Choose to shorten term (most interest savings)</li>
                  <li><strong>Interest saved: HKD 360,000+</strong></li>
                  <li><strong>Own your home ~4 years earlier</strong></li>
                </ul>
              </div>

              <h2>🏠 Is Buying Still Worth It? vs. Renting Analysis</h2>

              <div style={{
                background: '#f8fafc',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                margin: '20px 0',
                overflowX: 'auto'
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ background: '#eff6ff' }}>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #2563eb' }}>Metric</th>
                      <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #2563eb' }}>Buying</th>
                      <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #2563eb' }}>Renting</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>Initial Cash</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>HKD 886,250</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>HKD 39,000 (2 months deposit)</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>Monthly Cost</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>HKD 23,387</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>HKD 19,500 (typical for similar unit)</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>25-Year Total</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>HKD 7,902,350</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>HKD 5,889,000 (rent + inflation)</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>After 25 Years You Own</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0', color: '#10b981', fontWeight: 'bold' }}>Property worth ~HKD 10M+</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0', color: '#dc2626' }}>Nothing</td>
                    </tr>
                    <tr style={{ background: '#dcfce7' }}>
                      <td style={{ padding: '12px' }}><strong>Net Wealth After 25 Years</strong></td>
                      <td style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold', color: '#065f46' }}>+HKD 2-5M</td>
                      <td style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold', color: '#dc2626' }}>HKD 0</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div style={{
                background: '#eff6ff',
                padding: '20px',
                borderRadius: '8px',
                margin: '20px 0'
              }}>
                <h3 style={{ marginTop: 0 }}>🎯 The Verdict</h3>
                <p><strong>Buying makes sense if:</strong></p>
                <ul>
                  <li>You plan to stay 5+ years (break-even point)</li>
                  <li>You can comfortably afford the monthly payment</li>
                  <li>You want to build long-term wealth</li>
                  <li>You value stability and the freedom to renovate</li>
                </ul>
                <p style={{ marginBottom: 0 }}><strong>Renting makes sense if:</strong></p>
                <ul style={{ marginBottom: 0 }}>
                  <li>You need flexibility (job relocation, family changes)</li>
                  <li>You can invest the difference and earn {'>'}5% returns consistently</li>
                  <li>Property prices are overheated and may correct</li>
                  <li>You don't have sufficient savings for down payment + emergency fund</li>
                </ul>
              </div>

              <h2>🧮 Calculate Your Own True Cost</h2>
              <div style={{
                background: '#dcfce7',
                padding: '25px',
                borderRadius: '12px',
                border: '2px solid #10b981',
                margin: '40px 0',
                textAlign: 'center'
              }}>
                <h3 style={{ marginTop: 0, color: '#065f46' }}>💚 Free Mortgage Total Cost Calculator</h3>
                <p style={{ fontSize: '16px', marginBottom: '20px' }}>
                  Calculate the complete cost of your mortgage including interest, fees, stamp duty, and insurance. See exactly what you'll pay over the entire loan term.
                </p>
                <a
                  href="/"
                  style={{
                    display: 'inline-block',
                    padding: '14px 32px',
                    backgroundColor: '#10b981',
                    color: 'white',
                    textDecoration: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    fontSize: '16px',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#059669'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#10b981'}
                >
                  Calculate Now →
                </a>
              </div>

              <h2>❓ Frequently Asked Questions</h2>

              <div style={{ margin: '20px 0' }}>
                <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '8px', marginBottom: '15px', border: '1px solid #e2e8f0' }}>
                  <h4 style={{ marginTop: 0, color: '#2563eb' }}>Q1: Can I deduct mortgage interest from my taxes in Hong Kong?</h4>
                  <p style={{ marginBottom: 0 }}>
                    <strong>Yes, but only for owner-occupied properties.</strong> You can claim Home Loan Interest deduction up to HKD 100,000 per year for a maximum of 20 years. This can save you HKD 15,000-17,000 per year in taxes (at 15-17% marginal rate). Over 20 years, that's up to HKD 340,000 in tax savings!
                  </p>
                </div>

                <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '8px', marginBottom: '15px', border: '1px solid #e2e8f0' }}>
                  <h4 style={{ marginTop: 0, color: '#2563eb' }}>Q2: What if interest rates rise to 5% or 6%?</h4>
                  <p style={{ marginBottom: 0 }}>
                    At 5%, your monthly payment becomes HKD 26,338 (+HKD 2,951/month) and total interest jumps to HKD 3,401,400 (+HKD 885,300). At 6%, it's HKD 28,998/month and HKD 4,199,400 total interest. This is why choosing H-Rate with a good cap rate protection is crucial!
                  </p>
                </div>

                <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '8px', marginBottom: '15px', border: '1px solid #e2e8f0' }}>
                  <h4 style={{ marginTop: 0, color: '#2563eb' }}>Q3: Should I take a 25-year or 30-year mortgage?</h4>
                  <p style={{ marginBottom: 0 }}>
                    <strong>25 years:</strong> HKD 23,387/month, HKD 2,516,100 total interest<br />
                    <strong>30 years:</strong> HKD 21,231/month, HKD 3,143,160 total interest<br />
                    <br />
                    You save HKD 2,156/month with 30 years but pay HKD 627,060 more in interest. Choose 30 years if you need lower monthly payments now, but aim to make partial repayments later to save on interest.
                  </p>
                </div>

                <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '8px', marginBottom: '15px', border: '1px solid #e2e8f0' }}>
                  <h4 style={{ marginTop: 0, color: '#2563eb' }}>Q4: What's the biggest mistake first-time buyers make?</h4>
                  <p style={{ marginBottom: 0 }}>
                    <strong>Maxing out their budget!</strong> Many buyers calculate based on what they can "technically afford" per the stress test, leaving no room for emergencies, renovations, or lifestyle changes. Always maintain a buffer—ideally, your mortgage payment should be no more than 30-35% of your take-home income, not the maximum 50% the bank allows.
                  </p>
                </div>

                <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '8px', marginBottom: '15px', border: '1px solid #e2e8f0' }}>
                  <h4 style={{ marginTop: 0, color: '#2563eb' }}>Q5: How much should I budget for maintenance and repairs?</h4>
                  <p style={{ marginBottom: 0 }}>
                    Plan for 0.5-1% of property value annually for maintenance, management fees, and government rates/rent. For a HKD 5M property, that's HKD 25,000-50,000 per year or HKD 2,000-4,000 per month. Add this to your mortgage payment when calculating affordability!
                  </p>
                </div>
              </div>

              <AmazonRecommendation isEnglish={isEnglish} />

              <div style={{ marginTop: '50px', paddingTop: '30px', borderTop: '1px solid #e2e8f0' }}>
                <p style={{ fontSize: '14px', color: '#64748b' }}>
                  <strong>About PayWise365:</strong><br />
                  We provide free mortgage and loan calculation tools to help Hong Kong residents make informed financial decisions. No registration required, instant results, complete privacy.
                </p>
                <p style={{ fontSize: '14px', color: '#64748b' }}>
                  <strong>Author:</strong> PayWise365 Editorial Team<br />
                  <strong>Published:</strong> January 31, 2026<br />
                  <strong>Last Updated:</strong> January 31, 2026<br />
                  <strong>Next Update:</strong> April 2026 (quarterly updates)
                </p>
                <p style={{ fontSize: '12px', color: '#94a3b8', fontStyle: 'italic' }}>
                  <strong>Disclaimer:</strong> This article is for educational purposes only. Interest rates, stamp duty rates, and fees are based on 2026 market conditions and may change. Always consult with financial advisors and verify current rates with banks before making decisions.
                </p>
              </div>
            </>
          ) : (
            // Cantonese Content
            <>
              <div style={{ marginBottom: '30px', paddingBottom: '20px', borderBottom: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '10px' }}>
                  <span style={{
                    background: '#eff6ff',
                    color: '#2563eb',
                    padding: '6px 14px',
                    borderRadius: '14px',
                    fontWeight: '500',
                    marginRight: '12px'
                  }}>
                    按揭規劃
                  </span>
                  <span>發布日期：2026年1月31日</span>
                  <span style={{ margin: '0 8px' }}>•</span>
                  <span>閱讀時間：10分鐘</span>
                </div>
              </div>

              <h2>💰 震撼現實：你真正要付嘅金額</h2>
              <p>
                你買緊一個500萬港元嘅物業，首期一成。每月供款？大概18,200港元。睇落幾合理，對唔對？但係現實係：<strong>25年內，你會為呢個500萬嘅物業總共支付約840萬港元</strong>——比買入價貴咗差唔多70%！
              </p>
              <p>
                等我哋詳細拆解每一蚊去咗邊度，等你可以為人生最大嘅財務決定做好準備。
              </p>

              <div style={{
                background: '#fef3c7',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #fbbf24',
                margin: '30px 0'
              }}>
                <h3 style={{ marginTop: 0 }}>💡 快速總結</h3>
                <p><strong>500萬物業（450萬貸款，3.875%利率，25年）：</strong></p>
                <ul>
                  <li>首期：50萬</li>
                  <li>總利息：233.6萬</li>
                  <li>印花稅：16.875萬（首置）</li>
                  <li>律師費及銀行費用：6.5萬</li>
                  <li>按揭保險：15.75萬</li>
                  <li><strong>總開支：772.725萬港元</strong></li>
                </ul>
                <p style={{ marginBottom: 0 }}>
                  呢個係物業價錢嘅<strong>54.5%</strong>多！
                </p>
              </div>

              <h2>📊 完整成本分析：500萬港元物業</h2>

              <h3>情境：2026年首次置業人士</h3>
              <div style={{
                background: '#f0f9ff',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #7dd3fc',
                margin: '20px 0'
              }}>
                <ul>
                  <li><strong>物業價格：</strong>500萬港元</li>
                  <li><strong>首期：</strong>10%（50萬）</li>
                  <li><strong>貸款額：</strong>450萬</li>
                  <li><strong>利率：</strong>3.875%（2026年典型H按封頂利率）</li>
                  <li><strong>還款期：</strong>25年（300個月）</li>
                  <li><strong>每月供款：</strong>23,387港元</li>
                </ul>
              </div>

              <h2>💸 1. 本金：你借嘅錢</h2>
              <div style={{
                background: '#f8fafc',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                margin: '20px 0'
              }}>
                <p><strong>貸款額：450萬港元</strong></p>
                <p>呢個係最直接嘅——就係銀行借比你嘅錢。25年內，你通過每月供款慢慢還返呢筆錢。</p>
                <p style={{ marginBottom: 0 }}>
                  <strong>累計總額：450萬港元</strong>
                </p>
              </div>

              <h2>🏦 2. 利息：銀行嘅利潤</h2>
              <div style={{
                background: '#f8fafc',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                margin: '20px 0'
              }}>
                <p><strong>總利息（25年，3.875%）：251.61萬港元</strong></p>
                <p><strong>係你貸款額嘅56%！</strong></p>

                <h4>按年份劃分嘅利息支出：</h4>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', marginTop: '15px' }}>
                  <thead>
                    <tr style={{ background: '#eff6ff' }}>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #2563eb' }}>期間</th>
                      <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #2563eb' }}>利息支出</th>
                      <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #2563eb' }}>佔總額%</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: '10px', borderBottom: '1px solid #e2e8f0' }}>第1-5年</td>
                      <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>81.5萬</td>
                      <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>32.4%</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '10px', borderBottom: '1px solid #e2e8f0' }}>第6-10年</td>
                      <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>70.5萬</td>
                      <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>28.0%</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '10px', borderBottom: '1px solid #e2e8f0' }}>第11-15年</td>
                      <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>56.5萬</td>
                      <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>22.5%</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '10px', borderBottom: '1px solid #e2e8f0' }}>第16-20年</td>
                      <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>33萬</td>
                      <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>13.1%</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '10px' }}>第21-25年</td>
                      <td style={{ padding: '10px', textAlign: 'right' }}>10.1萬</td>
                      <td style={{ padding: '10px', textAlign: 'right' }}>4.0%</td>
                    </tr>
                  </tbody>
                </table>

                <p style={{ marginTop: '15px', marginBottom: 0 }}>
                  <strong>重要洞見：</strong>你喺頭幾年付最多利息。所以提早還款可以慳到最多錢！
                </p>
                <p style={{ marginBottom: 0, marginTop: '15px' }}>
                  <strong>累計總額：701.61萬港元</strong>
                </p>
              </div>

              <h2>📋 3. 印花稅：政府稅項</h2>
              <div style={{
                background: '#f8fafc',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                margin: '20px 0'
              }}>
                <p><strong>首次置業（從價印花稅第2標準稅率）：16.875萬港元（3.375%）</strong></p>

                <h4>香港印花稅率（2026年）：</h4>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', marginTop: '15px' }}>
                  <thead>
                    <tr style={{ background: '#eff6ff' }}>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #2563eb' }}>物業價值</th>
                      <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #2563eb' }}>首次置業</th>
                      <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #2563eb' }}>第二套房</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: '10px', borderBottom: '1px solid #e2e8f0' }}>300萬以下</td>
                      <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>100元</td>
                      <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>15%</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '10px', borderBottom: '1px solid #e2e8f0' }}>300-450萬</td>
                      <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>1.5%</td>
                      <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>15%</td>
                    </tr>
                    <tr style={{ background: '#dcfce7' }}>
                      <td style={{ padding: '10px', borderBottom: '1px solid #e2e8f0' }}><strong>450-600萬</strong></td>
                      <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}><strong>3.375%</strong></td>
                      <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}><strong>15%</strong></td>
                    </tr>
                    <tr>
                      <td style={{ padding: '10px', borderBottom: '1px solid #e2e8f0' }}>600-2000萬</td>
                      <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>4.25%</td>
                      <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>15%</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '10px' }}>2000萬以上</td>
                      <td style={{ padding: '10px', textAlign: 'right' }}>4.25%</td>
                      <td style={{ padding: '10px', textAlign: 'right' }}>15%</td>
                    </tr>
                  </tbody>
                </table>

                <div style={{
                  background: '#fef3c7',
                  padding: '15px',
                  borderRadius: '8px',
                  marginTop: '15px'
                }}>
                  <p style={{ marginTop: 0 }}><strong>⚠️ 重要：</strong></p>
                  <p style={{ marginBottom: 0 }}>
                    如果呢個係你嘅<strong>第二套住宅物業</strong>，你要付<strong>75萬港元（15%）</strong>——貴成58.125萬！所以物業投資者需要更深嘅資金池。
                  </p>
                </div>

                <p style={{ marginBottom: 0, marginTop: '15px' }}>
                  <strong>累計總額：718.485萬港元</strong>
                </p>
              </div>

              <h2>⚖️ 4. 律師費：樓宇買賣成本</h2>
              <div style={{
                background: '#f8fafc',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                margin: '20px 0'
              }}>
                <p><strong>典型範圍：1.5萬-2.5萬港元</strong></p>
                <p><strong>我哋嘅估算：2萬港元</strong></p>

                <h4>包括咩服務：</h4>
                <ul>
                  <li>業權查冊及核實</li>
                  <li>買賣合約審閱</li>
                  <li>按揭契約準備</li>
                  <li>土地註冊處登記</li>
                  <li>成交時法律代表</li>
                </ul>

                <p><strong>貼士：</strong>記得格價——律師費差異可以好大。問下你嘅銀行有冇推薦律師，佢哋通常有negotiated rates。</p>

                <p style={{ marginBottom: 0 }}>
                  <strong>累計總額：720.485萬港元</strong>
                </p>
              </div>

              <h2>🏢 5. 銀行及處理費用</h2>
              <div style={{
                background: '#f8fafc',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                margin: '20px 0'
              }}>
                <p><strong>典型總額：3萬-5萬港元</strong></p>
                <p><strong>我哋嘅估算：4萬港元</strong></p>

                <h4>分項：</h4>
                <ul>
                  <li><strong>物業估價：</strong>3,000-5,000港元</li>
                  <li><strong>按揭申請費：</strong>5,000-10,000港元（通常會豁免）</li>
                  <li><strong>火險：</strong>每年2,000-3,000 × 25年 = 50,000-75,000港元（upfront平均15,000港元）</li>
                  <li><strong>開戶/維護費：</strong>如果保持最低結餘通常會豁免</li>
                </ul>

                <p><strong>好消息：</strong>好多銀行喺推廣期會豁免申請費。現金回贈（典型0.5-1.2%貸款額）可以抵銷部分成本——可能拎返2.25萬-5.4萬！</p>

                <p style={{ marginBottom: 0 }}>
                  <strong>累計總額（扣除回贈）：722.235萬港元</strong>
                </p>
              </div>

              <h2>🛡️ 6. 按揭保險費（如適用）</h2>
              <div style={{
                background: '#f8fafc',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                margin: '20px 0'
              }}>
                <p><strong>按揭成數90%（450萬貸款，500萬物業）：15.75萬港元（貸款額3.5%）</strong></p>

                <h4>按揭證券公司保費（2026年）：</h4>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', marginTop: '15px' }}>
                  <thead>
                    <tr style={{ background: '#eff6ff' }}>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #2563eb' }}>按揭成數</th>
                      <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #2563eb' }}>25年期</th>
                      <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #2563eb' }}>30年期</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: '10px', borderBottom: '1px solid #e2e8f0' }}>80-85%</td>
                      <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>2.15%</td>
                      <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>2.47%</td>
                    </tr>
                    <tr style={{ background: '#dcfce7' }}>
                      <td style={{ padding: '10px', borderBottom: '1px solid #e2e8f0' }}><strong>85-90%</strong></td>
                      <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}><strong>3.5%</strong></td>
                      <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}><strong>4.35%</strong></td>
                    </tr>
                  </tbody>
                </table>

                <p style={{ marginTop: '15px' }}><strong>注意：</strong>如果你有能力付兩成首期（按揭成數80%），就可以完全避免按揭保險，慳返15.75萬！</p>

                <p style={{ marginBottom: 0 }}>
                  <strong>最終總額：737.985萬港元</strong>
                </p>
              </div>

              <h2>📊 最終賬單：完整成本總結</h2>
              <div style={{
                background: '#eff6ff',
                padding: '25px',
                borderRadius: '12px',
                border: '2px solid #2563eb',
                margin: '30px 0'
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '15px' }}>
                  <thead>
                    <tr style={{ background: '#dbeafe' }}>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #2563eb' }}>成本項目</th>
                      <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #2563eb' }}>金額（港元）</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #bfdbfe' }}>首期（10%）</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #bfdbfe' }}>500,000</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #bfdbfe' }}>貸款本金</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #bfdbfe' }}>4,500,000</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #bfdbfe' }}>總利息（25年）</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #bfdbfe', color: '#dc2626', fontWeight: 'bold' }}>2,516,100</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #bfdbfe' }}>印花稅（首置）</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #bfdbfe' }}>168,750</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #bfdbfe' }}>律師費</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #bfdbfe' }}>20,000</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #bfdbfe' }}>銀行及處理費</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #bfdbfe' }}>40,000</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #bfdbfe' }}>按揭保險（90%按揭成數）</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #bfdbfe' }}>157,500</td>
                    </tr>
                    <tr style={{ background: '#dbeafe' }}>
                      <td style={{ padding: '15px', fontWeight: 'bold', fontSize: '16px' }}>總計</td>
                      <td style={{ padding: '15px', textAlign: 'right', fontWeight: 'bold', fontSize: '18px', color: '#1e40af' }}>7,902,350</td>
                    </tr>
                  </tbody>
                </table>

                <div style={{
                  background: '#fef3c7',
                  padding: '20px',
                  borderRadius: '8px',
                  marginTop: '25px'
                }}>
                  <h3 style={{ marginTop: 0 }}>🎯 關鍵洞見：</h3>
                  <p style={{ fontSize: '18px', marginBottom: 0 }}>
                    你為一個<strong>500萬港元</strong>嘅物業支付<strong>790.235萬港元</strong>——比買入價貴咗<strong>58%</strong>，即係多咗<strong>290.235萬港元</strong>！
                  </p>
                </div>
              </div>

              <h2>💡 點樣減少你嘅總成本</h2>

              <div style={{
                background: '#dcfce7',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #10b981',
                margin: '20px 0'
              }}>
                <h3 style={{ marginTop: 0 }}>策略1：更高首期（兩成而非一成）</h3>
                <ul>
                  <li>貸款額：400萬（而非450萬）</li>
                  <li>慳到利息：約56萬</li>
                  <li>慳到按揭保險：15.75萬</li>
                  <li><strong>總節省：71.75萬港元</strong></li>
                </ul>
              </div>

              <div style={{
                background: '#dcfce7',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #10b981',
                margin: '20px 0'
              }}>
                <h3 style={{ marginTop: 0 }}>策略2：更短還款期（20年而非25年）</h3>
                <ul>
                  <li>每月供款：27,368港元（vs 23,387港元）</li>
                  <li>總利息：206.832萬（vs 251.61萬）</li>
                  <li><strong>慳到利息：44.778萬港元</strong></li>
                  <li><strong>提早5年供完！</strong></li>
                </ul>
              </div>

              <div style={{
                background: '#dcfce7',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #10b981',
                margin: '20px 0'
              }}>
                <h3 style={{ marginTop: 0 }}>策略3：更低利率（減0.5%）</h3>
                <ul>
                  <li>由3.875%降至3.375%</li>
                  <li>每月供款：22,367港元（慳1,020港元/月）</li>
                  <li>總利息：222.01萬（vs 251.61萬）</li>
                  <li><strong>慳到利息：29.6萬港元</strong></li>
                </ul>
                <p style={{ marginBottom: 0 }}>
                  <strong>貼士：</strong>記得格價！同時申請2-3間銀行拎最好嘅rate。
                </p>
              </div>

              <div style={{
                background: '#dcfce7',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #10b981',
                margin: '20px 0'
              }}>
                <h3 style={{ marginTop: 0 }}>策略4：第5年部分還款</h3>
                <ul>
                  <li>5年後做50萬部分還款</li>
                  <li>選擇縮短年期（最多利息節省）</li>
                  <li><strong>慳到利息：36萬+</strong></li>
                  <li><strong>提早約4年供完</strong></li>
                </ul>
              </div>

              <h2>🏠 買樓仍然值得嗎？vs 租樓分析</h2>

              <div style={{
                background: '#f8fafc',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                margin: '20px 0',
                overflowX: 'auto'
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ background: '#eff6ff' }}>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #2563eb' }}>項目</th>
                      <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #2563eb' }}>買樓</th>
                      <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #2563eb' }}>租樓</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>初始現金</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>88.625萬</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>3.9萬（兩個月按金）</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>每月成本</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>23,387</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>19,500（同類單位）</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>25年總額</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>790.235萬</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>588.9萬（租金+通脹）</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>25年後你擁有</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0', color: '#10b981', fontWeight: 'bold' }}>物業值約1000萬+</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0', color: '#dc2626' }}>零</td>
                    </tr>
                    <tr style={{ background: '#dcfce7' }}>
                      <td style={{ padding: '12px' }}><strong>25年後淨資產</strong></td>
                      <td style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold', color: '#065f46' }}>+200-500萬</td>
                      <td style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold', color: '#dc2626' }}>0港元</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div style={{
                background: '#eff6ff',
                padding: '20px',
                borderRadius: '8px',
                margin: '20px 0'
              }}>
                <h3 style={{ marginTop: 0 }}>🎯 結論</h3>
                <p><strong>買樓適合你如果：</strong></p>
                <ul>
                  <li>你計劃住5年以上（收支平衡點）</li>
                  <li>你有能力舒適地負擔每月供款</li>
                  <li>你想建立長遠財富</li>
                  <li>你重視穩定同裝修自由</li>
                </ul>
                <p style={{ marginBottom: 0 }}><strong>租樓適合你如果：</strong></p>
                <ul style={{ marginBottom: 0 }}>
                  <li>你需要彈性（工作調動、家庭變化）</li>
                  <li>你可以將差價投資並持續賺取{'>'}5%回報</li>
                  <li>樓價過熱，可能會調整</li>
                  <li>你未有足夠儲蓄做首期+應急資金</li>
                </ul>
              </div>

              <h2>🧮 計算你自己嘅真實成本</h2>
              <div style={{
                background: '#dcfce7',
                padding: '25px',
                borderRadius: '12px',
                border: '2px solid #10b981',
                margin: '40px 0',
                textAlign: 'center'
              }}>
                <h3 style={{ marginTop: 0, color: '#065f46' }}>💚 免費按揭總成本計算機</h3>
                <p style={{ fontSize: '16px', marginBottom: '20px' }}>
                  計算你嘅按揭完整成本，包括利息、費用、印花稅同保險。睇清楚成個還款期你會付幾多錢。
                </p>
                <a
                  href="/"
                  style={{
                    display: 'inline-block',
                    padding: '14px 32px',
                    backgroundColor: '#10b981',
                    color: 'white',
                    textDecoration: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    fontSize: '16px',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#059669'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#10b981'}
                >
                  立即計算 →
                </a>
              </div>

              <h2>❓ 常見問題</h2>

              <div style={{ margin: '20px 0' }}>
                <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '8px', marginBottom: '15px', border: '1px solid #e2e8f0' }}>
                  <h4 style={{ marginTop: 0, color: '#2563eb' }}>Q1：香港按揭利息可唔可以扣稅？</h4>
                  <p style={{ marginBottom: 0 }}>
                    <strong>可以，但只限自住物業。</strong>你可以申請居所貸款利息扣除，每年最多扣10萬港元，最多20年。如果你嘅邊際稅率係15-17%，每年可以慳稅1.5萬-1.7萬，20年總共可以慳到34萬！
                  </p>
                </div>

                <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '8px', marginBottom: '15px', border: '1px solid #e2e8f0' }}>
                  <h4 style={{ marginTop: 0, color: '#2563eb' }}>Q2：如果利率升到5%或6%會點？</h4>
                  <p style={{ marginBottom: 0 }}>
                    5%利率時，你每月供款變成26,338港元（+2,951/月），總利息跳升到340.14萬（+88.53萬）。6%時，供款變成28,998港元，總利息419.94萬。所以選擇有良好封頂保護嘅H按好重要！
                  </p>
                </div>

                <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '8px', marginBottom: '15px', border: '1px solid #e2e8f0' }}>
                  <h4 style={{ marginTop: 0, color: '#2563eb' }}>Q3：應該揀25年定30年按揭？</h4>
                  <p style={{ marginBottom: 0 }}>
                    <strong>25年：</strong>23,387港元/月，總利息251.61萬<br />
                    <strong>30年：</strong>21,231港元/月，總利息314.316萬<br />
                    <br />
                    30年慳2,156港元/月，但多付62.706萬利息。如果你而家需要較低每月負擔就揀30年，但記得之後做部分還款慳利息。
                  </p>
                </div>

                <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '8px', marginBottom: '15px', border: '1px solid #e2e8f0' }}>
                  <h4 style={{ marginTop: 0, color: '#2563eb' }}>Q4：首置人士最大嘅錯誤係咩？</h4>
                  <p style={{ marginBottom: 0 }}>
                    <strong>盡用供款能力！</strong>好多買家根據壓力測試計算佢哋「技術上可以負擔」嘅金額，冇留任何空間應付突發事件、裝修或者生活變化。記得保持buffer——理想情況下，你嘅按揭供款應該係實得收入嘅30-35%以下，而唔係銀行容許嘅最多50%。
                  </p>
                </div>

                <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '8px', marginBottom: '15px', border: '1px solid #e2e8f0' }}>
                  <h4 style={{ marginTop: 0, color: '#2563eb' }}>Q5：維修同保養應該預算幾多？</h4>
                  <p style={{ marginBottom: 0 }}>
                    預每年物業價值嘅0.5-1%用於維修、管理費同差餉地租。500萬嘅物業，即係每年2.5萬-5萬，或者每月2,000-4,000港元。計算負擔能力時記得加埋呢個！
                  </p>
                </div>
              </div>

              <AmazonRecommendation isEnglish={isEnglish} />

              <div style={{ marginTop: '50px', paddingTop: '30px', borderTop: '1px solid #e2e8f0' }}>
                <p style={{ fontSize: '14px', color: '#64748b' }}>
                  <strong>關於PayWise365：</strong><br />
                  我們提供免費的按揭及貸款計算工具，助你作出明智的財務決策。無需註冊，即時使用，保護你的私隱。
                </p>
                <p style={{ fontSize: '14px', color: '#64748b' }}>
                  <strong>作者：</strong>PayWise365 編輯團隊<br />
                  <strong>發布日期：</strong>2026年1月31日<br />
                  <strong>最後更新：</strong>2026年1月31日<br />
                  <strong>下次更新：</strong>2026年4月（按季度更新）
                </p>
                <p style={{ fontSize: '12px', color: '#94a3b8', fontStyle: 'italic' }}>
                  <strong>免責聲明：</strong>本文僅供教育用途。利率、印花稅率及費用基於2026年市場情況，可能會變動。作出決定前請諮詢財務顧問並向銀行核實最新利率。
                </p>
              </div>
            </>
          )}
        </Box>
      </Container>
    </Box>
  );
}
